/**
 * Stacked-axis handling for line datasets.
 *
 * Split out of `buildChartData`: Chart.js needs band-to-band fills and a numeric baseline on
 * every series of a stack, and those rules are a concern of their own.
 */

import type { MetricSeriesSpec } from './seriesTypes';

export const STACK_ID_LEFT = 'metricStackLeft';
export const STACK_ID_RIGHT = 'metricStackRight';

export function resolveLineFill(
    series: MetricSeriesSpec,
    isStacked: boolean,
    filledSeriesCountOnAxis: number
): boolean | 'origin' | '-1' | false {
    if (series.fill !== true) {
        return false;
    }

    if (!isStacked) {
        return true;
    }

    // Chart.js treats `fill: true` as "fill to origin". Stacked shares need
    // band-to-band fills; see `coalesceStackedNullsAsZero` for the null handling
    // that keeps the `-1` chain connected on sparse days.
    return filledSeriesCountOnAxis === 0 ? 'origin' : '-1';
}

export type StackableDataset = { data: (number | null)[]; yAxisID?: string; stack?: string };

/**
 * On stacked axes, Chart.js `fill: '-1'` needs a numeric baseline on every series
 * for a day. When at least one series has a value, absent series become `0`.
 * Days where every series is `null` stay gaps.
 */
export function coalesceStackedNullsAsZero(datasets: StackableDataset[]): void {
    const dayCount = datasets[0]?.data.length ?? 0;

    for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
        const hasAnyValue = datasets.some(
            (dataset) => dataset.data[dayIndex] !== null && dataset.data[dayIndex] !== undefined
        );

        if (!hasAnyValue) {
            continue;
        }

        for (const dataset of datasets) {
            if (dataset.data[dayIndex] === null || dataset.data[dayIndex] === undefined) {
                dataset.data[dayIndex] = 0;
            }
        }
    }
}

export function stackedDatasetsForAxis(
    datasets: StackableDataset[],
    axisKey: 'left' | 'right',
    stackId: string
): StackableDataset[] {
    const yAxisID = axisKey === 'right' ? 'yAxisRight' : 'yAxisLeft';

    return datasets.filter((dataset) => dataset.yAxisID === yAxisID && dataset.stack === stackId);
}
